import { forwardRef } from 'react';

import { DefaultComponentProps, MakeOptional } from '../../types';
import Checkbox, { CheckboxProps } from '../Checkbox';
import type { DropdownOption } from '../Dropdown';
import EmphasizedText from '../EmphasizedText/EmphasizedText';
import Flex from '../Flex';
import { Tooltip, TooltipProps } from '../Tooltip';

import { OptionContainer } from './MultiSelectGroup.styled';

const MIN_TOOLTIP_CHAR_LENGTH = 10;

type MultiSelectTooltipOptions = MakeOptional<TooltipProps, 'content'> & {
  /**
   * Minimum character length needed for tooltip to appear
   * For lengths less then this Tooltip will not appear
   */
  minTooltipCharLength?: number;
};

export type MultiSelectProps = DefaultComponentProps & {
  /**
   * This will be passed to each checkbox element.
   * As there can be multiple values set it's recommended to call name with array syntax ex. `fieldName[]`
   * */
  name?: string;
  options: DropdownOption[];
  searchTerm?: string;
  selectedValues?: string[];
  /**
   * @note
   * passing `tooltip: false` will hide the tooltip
   * */
  tooltip?: MultiSelectTooltipOptions | false;
  onChange?: CheckboxProps['onChange'];
};

/**
 * Primitive used by `MultiSelectGroup`.
 */
const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (props, ref) => {
    const {
      name,
      options,
      searchTerm = '',
      selectedValues,
      tooltip,
      onChange,
      ...rest
    } = props;

    const renderOption = (option: DropdownOption) => {
      const { value, label } = option;

      const checkbox = (
        <OptionContainer>
          <Checkbox
            checked={selectedValues?.includes(value)}
            key={value}
            label={<EmphasizedText match={searchTerm} text={label} />}
            name={name}
            value={value}
            onChange={onChange}
          />
        </OptionContainer>
      );

      if (tooltip === false) return checkbox;

      const {
        minTooltipCharLength = MIN_TOOLTIP_CHAR_LENGTH,
        ...tooltipProps
      } = tooltip || {};

      // Show tooltip only if the label length exceeds the threshold
      if (label.length <= minTooltipCharLength) {
        return checkbox;
      }

      return (
        <Tooltip content={label} side="right" {...tooltipProps}>
          {checkbox}
        </Tooltip>
      );
    };

    return (
      <Flex direction="column" ref={ref} {...rest}>
        {options.map(renderOption)}
      </Flex>
    );
  },
);

export default MultiSelect;
